/**
 * Adds zoom functionality to images while hovering.
 *
 * Remarks:
 * This will add a full screen container div at the beginning of the body element, which will be used by
 * all of the images for full screen mode.
 *
 * Requirements:
 * -- Image container --
 * - data-require="imageZoom" - Required for initialization.
 * - data-js-scale="{value}"  - Required on the image container in order to initialize it.
 *                              Value is a scale class name, for instance 'scale-150' or arbitrary 'scale-[2.2]'
 * - data-js-fullscreen-src   - OPTIONAL. The full screen image URL, as you may want it larger than the others.
 *                              If omitted, the image src will be used instead.
 *
 * -- Partial --
 * - ImageZoomRequiredMarkup.cshtml - Partial required inside the container, adds markup required by the zoom.
 *
 * Example:
 * <div data-require="imageZoom" data-js-scale="scale-[2.2]]" data-js-fullscreen-src="{URL_TO_FULLSCREEN_IMAGE}">
 *   <partial name="~/Features/ImageZoom/ImageZoomRequiredMarkup.cshtml" />
 *   <img .. /> or <picture> .. </picture>
 * </div>
 *
 */
export const imageZoom = () => {
  const imageContainers = document.querySelectorAll("[data-js-scale]");

  if (!imageContainers?.length) {
    return;
  }

  const fullScreenContainer = initializeFullScreenContainer();

  for (const imageContainer of imageContainers) {
    const scale = imageContainer.getAttribute("data-js-scale");
    initializeImageZoom(String(scale), imageContainer, fullScreenContainer);
  }
};

const mediaMobile = "(min-width: 0px) and (max-width: 767px)";
const mediaTablet = "(min-width: 768px) and (max-width: 1199px)";
const mediaSmallDesktop = "(min-width: 1200px) and (max-width: 1499px)";
const mediaFullDesktop = "(min-width: 1500px) and (max-width: 9999px)";

const floatNumberRegex = /(\d+(?:\.\d+)?)/;

const zoomEnabled = () => !window.matchMedia(mediaMobile).matches;
const isMultitouch = (e: TouchEvent) => e.touches.length > 1 || e.shiftKey;
const isTouchDevice = () => "ontouchstart" in window || navigator.maxTouchPoints > 0;

// Initializes the container div used to display images in full screen when clicked.
const initializeFullScreenContainer = () => {
  const fullScreen = document.createElement("div");
  fullScreen.classList.add(
    "hidden",
    "fixed",
    "overscroll-behavior",
    "overflow-hidden",
    "z-[500]",
    "top-0",
    "left-0",
    "w-[100vw]",
    "h-[100vh]",
    "bg-contain",
    "bg-no-repeat",
    "bg-black",
    "bg-center",
    "touch-pinch-zoom"
  );

  const closeFullscreen = () => {
    // We close full screen by going back in the browser (notice pushState() ), as this also resets the zoom level.
    // If we would simply close/hide the full screen element, the user would still be zoomed in
    // if they pinch-zoomed the image while in full screen mode.

    // Note: this assumes pushState() has been invoked when the full screen element was displayed,
    // and that the "popstate" event closes the fullscreen element.

    window.history.back();
  };

  const closeButton = document.querySelector(
    "[data-js-fullscreen-image-close]"
  ) as HTMLDivElement;

  // Clicking anywhere closes the full screen
  fullScreen.addEventListener("click", (e: MouseEvent) => {
    e.preventDefault();
    closeFullscreen();
  });

  // Enable "Esc" to close full screen
  document.addEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape" || event.key === "Esc") {
      closeFullscreen();
    }
  });

  // Allow closing by hitting Back button
  // Note that we close full screen mode by programmatically hitting the Back button in the closeFullscreen() function
  history.pushState(null, document.title, window.location.href);

  window.addEventListener("popstate", () => {
    fullScreen.style.backgroundImage = "";
    fullScreen.classList.remove("block");
    fullScreen.classList.add("hidden");

    body?.classList.remove("fixed", "w-full", "h-full");

    // Prevent going to previous page, we only want to close the fullscreen view
    history.pushState(null, document.title, window.location.href);
  });

  closeButton.classList.remove("hidden");
  fullScreen.prepend(closeButton);

  const body = document.querySelector("body");
  body?.prepend(fullScreen);

  return fullScreen;
};

// Calculates the current cursor position within the supplied image.
const getCursorPosition = (e: MouseEvent | TouchEvent, img: HTMLImageElement) => {
  const boundingClientRect = img.getBoundingClientRect();

  // Cursor position relative to the image ...
  let cursorX = -boundingClientRect.left;
  let cursorY = -boundingClientRect.top;

  if (e instanceof MouseEvent) {
    cursorX += e.pageX;
    cursorY += e.pageY;
  } else {
    cursorX += e.touches[0].clientX;
    cursorY += e.touches[0].clientY;
  }

  // ... with possible page scrolling.
  cursorX -= window.scrollX;
  cursorY -= window.scrollY;

  return { x: cursorX, y: cursorY };
};

/**
 * Gets the background position of the given HTML element with units.
 * @param element HTML Element.
 */
const getBackgroundPosition = (element: HTMLElement) => {
  const backgroundPosition = window
    .getComputedStyle(element, null)
    .backgroundPosition.trim()
    .split(/\s+/);

  return {
    left: parseFloat(backgroundPosition[0]),
    top: parseFloat(backgroundPosition[1]),
    units: {
      left: backgroundPosition[0].replace(floatNumberRegex, ""),
      top: backgroundPosition[1].replace(floatNumberRegex, ""),
    },
  };
};

const initializeImageZoom = (
  scale: string,
  imageContainer: Element,
  fullScreenContainer: HTMLDivElement
) => {
  const image = imageContainer.querySelector("img") as HTMLImageElement | undefined;
  if (!image) {
    console.warn(
      "Image container with attribute data-js-scale found, but it contains no image (img)."
    );

    return;
  }

  imageContainer.classList.add("relative", "overflow-hidden");

  const initializeInner = () => {
    const magnifier = document.createElement("div");
    magnifier.setAttribute("data-magnifier", "");
    magnifier.classList.add(
      "absolute",
      "top-0",
      "left-0",
      "w-full",
      "h-full",
      "bg-center",
      "bg-cover",
      "bg-no-repeat",
      "transition",
      "ease-out",
      "duration-500"
    );

    magnifier.setAttribute("data-js-magnifier", "");

    magnifier.style.cursor = "zoom-in";
    magnifier.style.backgroundImage = `url('${image.currentSrc}')`; // Note: we can't check src attribute, since the actual image from the <picture> element varies depending on screen size
    magnifier.style.maxWidth = `${image.naturalWidth}px`; // Image div should never be larger than the original image size.
    magnifier.style.maxHeight = `${image.naturalHeight}px`;

    imageContainer.prepend(magnifier);

    const moveHandler = (e: MouseEvent) => {
      e.preventDefault();
      if (!zoomEnabled()) {
        return;
      }
      const pos = getCursorPosition(e, image);
      const transX = (pos.x / magnifier.scrollWidth) * 100;
      const transY = (pos.y / magnifier.scrollHeight) * 100;
      magnifier.style.transformOrigin = `${transX}% ${transY}%`;
    };
    magnifier.addEventListener("mousemove", moveHandler);

    const enterHandler = (e: MouseEvent) => {
      e.preventDefault();
      if (!zoomEnabled()) {
        return;
      }
      magnifier.classList.remove("scale-100");
      magnifier.classList.add(scale);
    };
    magnifier.addEventListener("mouseenter", enterHandler);

    const leaveHandler = (e: MouseEvent) => {
      e.preventDefault();
      if (!zoomEnabled()) {
        return;
      }
      magnifier.classList.remove(scale);
      magnifier.classList.add("scale-100");
    };
    magnifier.addEventListener("mouseleave", leaveHandler);

    // For triggering full screen mode.
    magnifier.addEventListener("click", (e: MouseEvent | TouchEvent) => {
      e.preventDefault();

      fullScreenContainer.style.cursor = zoomEnabled() ? "zoom-out" : "default";

      // Disable background scrolling.
      const body = document.querySelector("body");
      body?.classList.add("fixed", "w-full", "h-full");

      const imageSrc =
        imageContainer.getAttribute("data-js-fullscreen-src") ?? image.currentSrc;
      fullScreenContainer.style.backgroundImage = `url('${imageSrc}')`;
      fullScreenContainer.classList.remove("hidden");
      fullScreenContainer.classList.add("block");

      // Set initial background position for the image, so it doesn't jump to the top
      if (isTouchDevice()) {
        // Image params
        const urlParams = new URLSearchParams(imageSrc);
        const originalImageWidth = parseInt(urlParams.get("width") ?? "");
        const originalImageHeight = parseInt(urlParams.get("height") ?? "");

        // Check if parseInt returned NaN
        if (!originalImageWidth || !originalImageHeight) {
          console.warn("Could not get the original image size.");
        } else {
          // Background size is set to "contain", that's why we only have to fix lower value
          let offsetTop = 0,
            offsetLeft = 0;
          if (
            Math.abs(1 - originalImageWidth / window.innerWidth) <
            Math.abs(1 - originalImageHeight / window.innerHeight)
          ) {
            offsetLeft =
              (window.innerWidth -
                originalImageWidth / (originalImageHeight / window.innerHeight)) /
              2;
          } else {
            offsetTop =
              (window.innerHeight -
                originalImageHeight / (originalImageWidth / window.innerWidth)) /
              2;
          }

          fullScreenContainer.style.backgroundPosition = `${
            offsetLeft > 0 ? offsetLeft : 0
          }px ${offsetTop > 0 ? offsetTop : 0}px`;
        }
      }

      let moveStartX = 0,
        moveStartY = 0;
      let backgroundPositionX = 0,
        backgroundPositionY = 0;
      const touchStartHandler = (e: TouchEvent) => {
        if (e.target !== fullScreenContainer) {
          return;
        }

        // For multitouch we allow default.
        if (isMultitouch(e)) {
          return;
        }

        e.preventDefault();
        moveStartX = e.touches[0].clientX;
        moveStartY = e.touches[0].clientY;

        const backgroundPosition = getBackgroundPosition(fullScreenContainer);
        if (
          (backgroundPosition.top || backgroundPosition.left) &&
          (!backgroundPosition.units.top.match("px") ||
            !backgroundPosition.units.left.match("px"))
        ) {
          console.warn("Background position should be in pixels.");
          return;
        }

        backgroundPositionX = backgroundPosition.left;
        backgroundPositionY = backgroundPosition.top;
      };

      fullScreenContainer.addEventListener("touchstart", touchStartHandler);

      const touchMoveHandler = (e: TouchEvent) => {
        // For multitouch we allow default - zooming.
        if (isMultitouch(e)) {
          return;
        }

        e.preventDefault();

        const diffX = e.touches[0].clientX - moveStartX;
        const diffY = e.touches[0].clientY - moveStartY;

        fullScreenContainer.style.backgroundPosition = `${
          backgroundPositionX + diffX
        }px ${backgroundPositionY + diffY}px`;
      };

      fullScreenContainer.addEventListener("touchmove", touchMoveHandler);

      // Reset zoom as it may otherwise hang on touch devices exiting full screen mode.
      magnifier.classList.remove(scale);
      magnifier.classList.add("scale-100");
    });
  };

  // Updates information text tag, it differs if zoom is enabled or not.
  const setInfoText = () => {
    const infoTextAttribute = zoomEnabled()
      ? "data-js-info-zoom-enabled"
      : "data-js-info-zoom-disabled";
    const textElements = document.querySelectorAll(`[${infoTextAttribute}]`);

    if (!textElements?.length) {
      return;
    }

    for (const textElement of textElements) {
      textElement.innerHTML = textElement.getAttribute(infoTextAttribute) ?? "";
    }
  };

  // Images need to be loaded before initializing zoom, but some images are lazy.
  const lazyInit = () => {
    imageContainer.querySelectorAll("[data-js-magnifier]").forEach(e => e.remove()); // May be a reinitialize.
    setInfoText();

    if (image.complete) {
      initializeInner();
    } else {
      image.addEventListener("load", initializeInner);
    }
  };

  // Since the image size changes with the media query, we need to reinitialize on each break.
  window.matchMedia(mediaMobile).addEventListener("change", lazyInit);
  window.matchMedia(mediaTablet).addEventListener("change", lazyInit);
  window.matchMedia(mediaSmallDesktop).addEventListener("change", lazyInit);
  window.matchMedia(mediaFullDesktop).addEventListener("change", lazyInit);

  lazyInit();
};
